import React from 'react'
import * as ReactDOM from 'react-dom/client';

import styleClasses from './Toast.module.css';

import clsx from "clsx";

type ToastType = 'default' | 'success' | 'error' | 'warning' | 'info';

interface ToastProps {
  title: string;
  undoAction?: () => void;
  duration?: number;
  type?: ToastType;
}

export default function ToastComp({ title, undoAction, duration = 2000, type = 'default' }: ToastProps) {

    const [open, setOpen] = React.useState(false);
    const [isClosing, setIsClosing] = React.useState(false);

    React.useEffect(() => {
        // 显示动画
        const showTimer = setTimeout(() => {
            setOpen(true);
        }, 100);

        // 自动关闭
        const closeTimer = setTimeout(() => {
            handleClose();
        }, duration);

        return () => {
            clearTimeout(showTimer);
            clearTimeout(closeTimer);
        };
    }, [duration]);

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setOpen(false);
        }, 150); // 动画时间
    };

    const getTypeStyles = () => {
        switch (type) {
            case 'success':
                return 'bg-green-50 border-green-200 text-green-800';
            case 'error':
                return 'bg-red-50 border-red-200 text-red-800';
            case 'warning':
                return 'bg-yellow-50 border-yellow-200 text-yellow-800';
            case 'info':
                return 'bg-blue-50 border-blue-200 text-blue-800';
            default:
                return '';
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return '✓';
            case 'error':
                return '✕';
            case 'warning':
                return '⚠';
            case 'info':
                return 'ℹ';
            default:
                return null;
        }
    };

    if (!open) return null;

    return (
        <div className={styleClasses["lc-toast-viewport"]}>
            <div
                className={clsx(
                    styleClasses["toast-root"],
                    "lc-base",
                    getTypeStyles()
                )}
                data-state={isClosing ? 'closed' : 'open'}
            >
                <div className="flex items-center gap-2">
                    {getIcon() && <span className="font-bold text-lg">{getIcon()}</span>}
                    <span>{title}</span>
                </div>
                {undoAction ? (
                    <button 
                        className={clsx(styleClasses["undo-btn"], "whitespace-nowrap")} 
                        onClick={() => {
                            undoAction();
                            handleClose();
                        }}
                    >
                            撤销
                        </button>
                ) : null}
            </div>
        </div>
    )
}

function showToast(arg: string | ToastProps, defaultType?: ToastType) { 

    const { title, undoAction = undefined, duration = 2000, type = defaultType || 'default' } = (() => {
        if(typeof(arg) == "string") {
            return { title: arg }
        } else {
            return arg
        }
    })();

    const toastElem = document.createElement('div');

    document.body.appendChild(toastElem);

    const root = ReactDOM.createRoot(toastElem);
    root.render(<ToastComp {...{ title, undoAction, duration, type }} />);

    setTimeout(() => {
        try {
            // 先卸载 React 组件
            root.unmount();
        } catch (e) {
            console.warn('Error unmounting toast:', e);
        }

        // 延迟移除 DOM，确保 React 完成清理
        setTimeout(() => {
            try {
                if (toastElem && toastElem.parentNode === document.body) {
                    document.body.removeChild(toastElem);
                }
            } catch (e) {
                console.warn('Error removing toast element:', e);
            }
        }, 50);
    }, duration + 200);

}

// 导出带类型方法的 toast 对象
export const toast = Object.assign(
    (arg: string | ToastProps) => showToast(arg),
    {
        success: (message: string, options?: Omit<ToastProps, 'title' | 'type'>) => 
            showToast({ title: message, ...options }, 'success'),
        
        error: (message: string, options?: Omit<ToastProps, 'title' | 'type'>) => 
            showToast({ title: message, ...options }, 'error'),
        
        warning: (message: string, options?: Omit<ToastProps, 'title' | 'type'>) => 
            showToast({ title: message, ...options }, 'warning'),
        
        info: (message: string, options?: Omit<ToastProps, 'title' | 'type'>) => 
            showToast({ title: message, ...options }, 'info'),
    }
);
